import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { Box, Divider, useTheme } from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";
import useTrackTypesFilterOptions from "@/modules/common/hooks/lookups/track-types-filter-options.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import {
  addTrackDetailedSchema,
  mandationPerTrackType,
  PROPERTY_TRACKS,
} from "@/modules/property-tracks/utils";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import PropertyDetailsForm from "../../track-actions/add-track-detailed/property-details-form";
import { useAddSubproperty } from "@/modules/property-tracks/hooks/add-sub-property.hook";
import { Compound, RentalTag } from "@/modules/property-tracks/types";
import { useEffect, useMemo, useState } from "react";
import PriceDetailsForm from "../../track-actions/add-track-detailed/price-details/price-details-form";
import { enforceMandates, MandateFields } from "@/modules/common/utils/zod";
import { PriceRoles } from "@/modules/property-tracks/dtos";

type AddTrackDetailedFormFields = z.infer<
  ReturnType<typeof addTrackDetailedSchema>
>;

const AddSubPropertyForm = ({
  propertyUid,
  modalOpen,
  setModalOpen,
  compound,
  latitude,
  longitude,
  address,
  rentalTag,
  isFinishingTrack,
}: {
  propertyUid: string;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  compound?: Compound;
  latitude?: number;
  longitude?: number;
  address?: string;
  rentalTag?: RentalTag;
  isFinishingTrack?: boolean;
}) => {
  const theme = useTheme();
  const { data: trackTypes, isLoading: TrackTypesLoading } =
    useTrackTypesFilterOptions();

  const [trackType, setTrackType] = useState<PROPERTY_TRACKS | undefined>();
  const [mandates, setMandates] =
    useState<MandateFields<AddTrackDetailedFormFields>>();

  const addTrackDetailedForm = useForm<AddTrackDetailedFormFields>({
    resolver: zodResolver(
      addTrackDetailedSchema().superRefine((data, ctx) => {
        if (mandates) enforceMandates(data, mandates, ctx, []);
      }),
    ),
    defaultValues: {
      property: {
        compound: compound
          ? { label: compound?.name, value: compound.id }
          : undefined,
        latitude,
        longitude,
        address,
      },
    },
  });

  const { handleSubmit, reset, control, watch, setValue, trigger } =
    addTrackDetailedForm;

  const selectedTrackType = useMemo(
    () => watch("trackType"),
    [watch("trackType")],
  );

  useEffect(() => {
    if (selectedTrackType?.label as PROPERTY_TRACKS) {
      const track = selectedTrackType?.label as PROPERTY_TRACKS;
      setTrackType(track);
      setMandates(mandationPerTrackType[track]);
    }
    setValue("property.propertyPrice", undefined);
  }, [selectedTrackType]);

  useEffect(() => {
    if (mandates) trigger();
  }, [mandates]);

  const onSubmit: SubmitHandler<AddTrackDetailedFormFields> = async (data) => {
    submitAddSubproperty(data);
  };

  const mutationAddSubProperty = useAddSubproperty();

  const submitAddSubproperty = (data: AddTrackDetailedFormFields) => {
    mutationAddSubProperty.mutate(
      {
        uid: propertyUid,
        data: {
          trackTypeId: data.trackType.value,
          property: {
            propertyTypeId: data.property.propertyType?.value,
            unitId: data.property.unitId,
            numberOfBedrooms: data.property.numberOfBedrooms,
            numberOfBathrooms: data.property.numberOfBathrooms,
            unitArea: data.property.unitArea,
            phase: data.property.phase,
            landArea: data.property.landArea,
            gardenArea: data.property.gardenArea,
            roofArea: data.property.roofArea,
            isUnderConstruction: data.property.isUnderConstruction,
            floorNumber: data.property.floorNumber,
            numberOfFloors: data.property.numberOfFloors,
            deliveryDate: data.property.readyToMove
              ? undefined
              : data.property.deliveryDate,
            readyToMove: data.property.readyToMove,
            description: data.property.description ?? undefined,
            finishing: data.property.finishing?.value,
            amenityIds: [...(data.property.amenityIds?.values() ?? [])],
            propertyPrice:
              Object.keys(data.property.propertyPrice ?? {}).length > 0
                ? {
                    ...data.property.propertyPrice,
                    installmentFrequency:
                      data.property.propertyPrice?.installmentFrequency?.value,
                    priceRole: PriceRoles.ASKING,
                  }
                : undefined,
            latitude: data.property.latitude,
            longitude: data.property.longitude,
            address: data.property.address,
          },
        },
      },
      {
        onSuccess: () => {
          handleCloseModal();
          showToast({
            type: ToastType.SUCCESS,
            message: `Sub Property added successfully.`,
            toastId: TOAST_IDS.ADD_SUB_PROPERTY_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.ADD_SUB_PROPERTY_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      },
    );
  };
  const resetFormState = () => {
    reset();
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    resetFormState();
  };

  return (
    <>
      <EditFieldModal
        open={modalOpen}
        setOpen={setModalOpen}
        header="Add Sub Property"
        width={480}
        handleConfirm={handleSubmit(onSubmit)}
        handleCloseModal={handleCloseModal}
        isLoading={mutationAddSubProperty.isPending}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.md,
          }}
        >
          <Controller
            control={control}
            name="trackType"
            render={({ field, fieldState }) => (
              <CustomAutoComplete
                options={(() => {
                  let filtered = trackTypes ?? [];
                  if (isFinishingTrack && rentalTag === "hospitality") {
                    filtered = filtered.filter(
                      (t) => t.name === PROPERTY_TRACKS.HOSPITALITY,
                    );
                  } else if (isFinishingTrack && rentalTag === "longTerm") {
                    filtered = filtered.filter(
                      (t) => t.name === PROPERTY_TRACKS.RENTAL,
                    );
                  } else {
                    filtered = filtered.filter(
                      (t) => t.name !== PROPERTY_TRACKS.FINISHING,
                    );
                  }
                  return filtered.map((type) => ({
                    label: type.name,
                    value: type.ids[0]?.toString() || "",
                  }));
                })()}
                required
                loading={TrackTypesLoading}
                placeholder="Property Track"
                label="Property Track"
                onChange={(value) => field.onChange(value)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              ></CustomAutoComplete>
            )}
          ></Controller>

          <PropertyDetailsForm
            form={addTrackDetailedForm}
            namePrefix="property"
            disabledFields={["compound"]}
            trackTypeName={trackType}
          />
          {trackType === PROPERTY_TRACKS.RESALE && (
            <>
              <Divider sx={{ my: theme.nawy.spacing.lg }} />
              <PriceDetailsForm
                form={addTrackDetailedForm}
                namePrefix="property.propertyPrice"
              />
            </>
          )}
        </Box>
      </EditFieldModal>
    </>
  );
};

export default AddSubPropertyForm;
